import { BasicData } from 'models/husqiotbus';

const ACCEPTOR_TEMPLATE =
  '<acceptor name="{name}"><attribute attribute-context="{attributeContext}">{attributeValue}</attribute><expression>{expression}</expression></acceptor>';
const ACCEPTOR_ANY_TEMPLATE = '<acceptor name="{name}"><any></any></acceptor>';
const AUTHORIZER_TEMPLATE =
  '<authorizer name="{name}"><token-authorizer>{systemsXml}</token-authorizer></authorizer>';
const SYSTEM_TEMPLATE = '<system ordinality="optional">{system}</system>';
const AUTHORIZER_ROLE_TEMPLATE =
  '<authorizer name="{name}"><role-token-authorizer/></authorizer>';
const AUTHORIZER_ROLE_WITH_SYSTEMS_TEMPLATE =
  '<authorizer name="{name}"><role-token-authorizer>{systemsXml}</role-token-authorizer></authorizer>';
const ROLE_FEATURE_TEMPLATE =
  '<feature name="{featureName}" access-level="{accessLevel}"/>';
const ROLE_TEMPLATE =
  '<token-authorizer-role role-name="{roleName}">{features}</token-authorizer-role>';
const VALIDATOR_TEMPLATE =
  '<validator name="{name}"><attribute attribute-context="{attributeContext}">{attributeValue}</attribute><expression>{expression}</expression></validator>';
const VALIDATOR_TEMPLATE_WITH_ORDINALITY =
  '<validator name="{name}"><attribute ordinality="{ordinality}" attribute-context="{attributeContext}">{attributeValue}</attribute><expression>{expression}</expression></validator>';
const ANNOTATOR_TEMPLATE_WITH_KEY =
  '<annotator name="{name}"><key-annotator key-space="{keySpace}"><key-attribute ordinality="optional" attribute-context="{attributeContext}">{keyAttributeValue}</key-attribute>{attributes}</key-annotator></annotator>';
const ATTRIBUTE_TEMPALATE = '<attribute>{attributeValue}</attribute>';
const ANNOTATOR_TEMPLATE_WITH_SYSTEM =
  '<annotator name="{name}"><system-annotator><key-attribute ordinality="optional" attribute-context="{attributeContext}">{keyAttributeValue}</key-attribute></system-annotator></annotator>';
const ANNOTATOR_TEMPLATE_WITH_WEBSOCKET =
  '<annotator name="{name}"><websocket-annotator user-prefix="{userPrefix}"><key-attribute ordinality="optional">{keyAttributeValue}</key-attribute><system>{system}</system></websocket-annotator></annotator>';
const ANNOTATOR_TEMPLATE_STATIC =
  '<annotator name="{name}"><static-annotator><attribute>{attribute}</attribute><value>{value}</value></static-annotator></annotator>';

export function generateBasicDataXml(basicData: BasicData) {
  const annotorsXml = generateAnnotorsXml(basicData);
  const acceptorsXml = generateAcceptorsXml(basicData);
  const rolesXml = generateRolesXml(basicData);
  const authorizersXml = generateAuthorizersXml(basicData);
  const validatorsXml = generateValidatorsXml(basicData);
  return `${annotorsXml}${rolesXml}${authorizersXml}${validatorsXml}${acceptorsXml}`;
}

function generateAcceptorsXml(basicData: BasicData): string {
  const acceptorsBasicData = basicData.acceptors.map((acceptor) => {
    if (!acceptor.attributeContext || acceptor.attributeContext === '') {
      const acceptorXml = ACCEPTOR_ANY_TEMPLATE;
      return acceptorXml.replace('{name}', acceptor.name);
    }
    const acceptorXml = ACCEPTOR_TEMPLATE;
    return acceptorXml
      .replace('{name}', acceptor.name)
      .replace('{attributeContext}', acceptor.attributeContext)
      .replace('{attributeValue}', acceptor.attributeValue || '')
      .replace('{expression}', acceptor.expression || '');
  });
  const acceptorsBasicDataXml = `<acceptors>${acceptorsBasicData.join(
    ''
  )}</acceptors>`;
  return acceptorsBasicDataXml;
}

function generateRolesXml(basicData: BasicData): string {
  const rolesBasicData = basicData.roles.map((role) => {
    const roleXml = ROLE_TEMPLATE;
    const roleFeatureXml = ROLE_FEATURE_TEMPLATE;
    const featureXmls = role.features.map((feature) =>
      roleFeatureXml
        .replace('{featureName}', feature.featureName)
        .replace('{accessLevel}', feature.accessLevel)
    );
    return roleXml
      .replace('{roleName}', role.roleName)
      .replace('{features}', featureXmls.join(''));
  });
  return `<token-authorizer-roles>${rolesBasicData.join(
    ''
  )}</token-authorizer-roles>`;
}

function generateAuthorizersXml(basicData: BasicData): string {
  const autherizersBasicData = basicData.authorizers.map((autherizer) => {
    const systemsXml =
      autherizer.systems
        ?.map((system) => SYSTEM_TEMPLATE.replace('{system}', system))
        .join('') || '';

    if (autherizer.role && autherizer.systems) {
      const autherizerXml = AUTHORIZER_ROLE_WITH_SYSTEMS_TEMPLATE;
      return autherizerXml
        .replace('{name}', autherizer.name)
        .replace('{systemsXml}', systemsXml);
    } else if (autherizer.role) {
      const autherizerXml = AUTHORIZER_ROLE_TEMPLATE;
      return autherizerXml.replace('{name}', autherizer.name);
    } else {
      const autherizerXml = AUTHORIZER_TEMPLATE;
      const authorizerSystems = autherizer.allSystems
        ? '<all-systems/>'
        : systemsXml;
      return autherizerXml
        .replace('{name}', autherizer.name)
        .replace('{systemsXml}', authorizerSystems);
    }
  });
  return `<authorizers>${autherizersBasicData.join('')}</authorizers>`;
}

function generateValidatorsXml(basicData: BasicData): string {
  const validatorBasicData = basicData.validators.map((validator) => {
    if (validator.ordinality) {
      const validatorXml = VALIDATOR_TEMPLATE_WITH_ORDINALITY;
      return validatorXml
        .replace('{name}', validator.name)
        .replace('{attributeContext}', validator.attributeContext)
        .replace('{ordinality}', validator.ordinality)
        .replace('{attributeValue}', validator.attributeValue)
        .replace('{expression}', validator.expression);
    }
    const validatorXml = VALIDATOR_TEMPLATE;
    return validatorXml
      .replace('{name}', validator.name)
      .replace('{attributeContext}', validator.attributeContext)
      .replace('{attributeValue}', validator.attributeValue)
      .replace('{expression}', validator.expression);
  });
  return `<validators>${validatorBasicData.join('')}</validators>`;
}

function generateAnnotorsXml(basicData: BasicData): string {
  const annotatorBasicData = basicData.annotators.map((annotator) => {
    if (annotator.keyAnnotator) {
      const annotatorXml = ANNOTATOR_TEMPLATE_WITH_KEY;
      const attributeXml = annotator.keyAnnotator.attributeValues?.map((attr) =>
        ATTRIBUTE_TEMPALATE.replace('{attributeValue}', attr)
      );
      return annotatorXml
        .replace('{name}', annotator.name)
        .replace('{keySpace}', annotator.keyAnnotator.keySpace)
        .replace(
          '{attributeContext}',
          // eslint-disable-next-line prettier/prettier
          annotator.keyAnnotator.keyAttribute.attributeContext || 'application-properties'
        )
        .replace(
          '{keyAttributeValue}',
          annotator.keyAnnotator.keyAttribute.value
        )
        .replace('{attributes}', attributeXml?.join('') || '');
    } else if (annotator.systemAnnotator) {
      const annotatorXml = ANNOTATOR_TEMPLATE_WITH_SYSTEM;
      return annotatorXml
        .replace('{name}', annotator.name)
        .replace(
          '{attributeContext}',
          // eslint-disable-next-line prettier/prettier
          annotator.systemAnnotator.keyAttribute.attributeContext || 'application-properties'
        )
        .replace(
          '{keyAttributeValue}',
          annotator.systemAnnotator.keyAttribute.value
        );
    } else if (annotator.websocketAnnotator) {
      const annotatorXml = ANNOTATOR_TEMPLATE_WITH_WEBSOCKET;
      return annotatorXml
        .replace('{name}', annotator.name)
        .replace('{userPrefix}', annotator.websocketAnnotator.userPrefix)
        .replace(
          '{keyAttributeValue}',
          annotator.websocketAnnotator.keyAttribute.value
        )
        .replace('{system}', annotator.websocketAnnotator.system);
    } else if (annotator.staticAnnotator) {
      const annotatorXml = ANNOTATOR_TEMPLATE_STATIC;
      return annotatorXml
        .replace('{name}', annotator.name)
        .replace('{attribute}', annotator.staticAnnotator.attribute)
        .replace('{value}', annotator.staticAnnotator.value);
    } else {
      throw new Error(
        `Can not generate XML for unknown annotator ${JSON.stringify(
          annotator
        )}`
      );
    }
  });
  return `<annotators>${annotatorBasicData.join('')}</annotators>`;
}
